import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Button, Divider, Stack, Tooltip, Typography } from '@mui/material'
import type { ReceptorRecord } from '@trustero/trustero-api-web/lib/agent/receptor_pb'
import { ModalForm, ModalFormId } from 'src/components/ModalForms'
import { GridColumnSortType } from 'src/components/Reusable/Grid/GridColumnSort/GridColumnSort.constants'
import { getNextColSortType } from 'src/components/Reusable/Grid/GridColumnSort/GridColumnSort.utils'
import {
  useHideModal,
  useIsShowModal,
  useSetActiveModal,
} from 'src/Modal/ModalStateContext'
import {
  PrimaryButton,
  SecondaryButton,
  StandardButtonSize,
} from 'src/components/Reusable/Buttons'
import { showInfoToast } from 'src/Utils/helpers/toast'
import { getReceptorTemplate } from 'src/xgenerated'
import {
  blockIfReceptorNeedsCredentials,
  credentialsActionForPickerSource,
  receptorNeedsCredentials,
} from 'src/pages/Receptors/folderScanReceptor.helpers'
import {
  useAccountReceptors,
  useInvalidateReceptorsCache,
} from 'src/components/async/model/receptor/useReceptors'
import {
  documentSourceLabel,
  hasSeveralConnections,
} from 'src/pages/Receptors/documentSourceLabel.helpers'
import { useDocumentPicker } from './DocumentPickerContext'
import { useDocumentSourceInfo, useDocumentTree } from './DocumentPicker.hooks'
import { documentSourceSpec } from './DocumentPicker.providers'
import {
  ConnectionSelect,
  ListPane,
  PathBreadcrumbs,
  ProblemBanner,
  SearchBox,
  TreePane,
  ViewToggle,
  sortNodes,
  ListSortColumn,
} from './DocumentPicker.components'
import {
  BrowserRoot,
  FooterActions,
  RightPane,
  RightPaneDivider,
  RightPaneFooter,
  RightPaneHeader,
} from './DocumentPicker.styles'
import {
  DocumentNode,
  DocumentPickerSource,
  ROOT_PARENT_ID,
  isContainerLike,
  isLinkable,
} from './DocumentPicker.types'

const parentModalFor = (
  source: DocumentPickerSource,
  subjectId?: string,
): ModalFormId => {
  switch (source) {
    case 'FolderScan':
      return ModalFormId.ADD_FOLDER_SCAN
    case 'Policy':
      return ModalFormId.ADD_DOCUMENT
    default:
      return subjectId
        ? ModalFormId.ADD_EVIDENCE
        : ModalFormId.ADD_EVIDENCE_GROUPS
  }
}

/**
 * The document picker. One modal for every document receptor: it browses the
 * receptor's repository through ntrced, one level at a time, under the
 * receptor's own identity, and hands the picked nodes back through
 * DocumentPickerContext.
 */
export const DocumentPickerModal = (): JSX.Element | null => {
  const { request, setSelection } = useDocumentPicker()
  const isVisible = useIsShowModal(ModalFormId.DOCUMENT_PICKER)
  const hide = useHideModal({ modalId: ModalFormId.DOCUMENT_PICKER })

  // The connection being browsed: the one the request named, until the
  // person switches to another connection of the same model in the header.
  const [switchedReceptor, setSwitchedReceptor] = useState<
    ReceptorRecord | undefined
  >(undefined)
  const receptor = switchedReceptor ?? request?.receptor
  const receptorId = receptor?.getId()
  const modelId = receptor?.getModelid() || ''
  const spec = useMemo(() => documentSourceSpec(modelId), [modelId])

  const source: DocumentPickerSource = request?.source || 'Evidence'
  const mode = request?.mode || 'files'
  const foldersMode = mode === 'folders'
  const parentModal =
    request?.parentModalId || parentModalFor(source, request?.subjectId)
  const showParentModal = useSetActiveModal(parentModal)
  const invalidateReceptors = useInvalidateReceptorsCache()

  const receptorBlocked = receptorNeedsCredentials(receptor)
  const active = isVisible && !!receptorId && !receptorBlocked

  const { data: sourceInfo } = useDocumentSourceInfo(receptorId, active)
  const identity = sourceInfo?.getIdentity() || ''
  const noticeContext = useMemo(() => {
    const credentials: Record<string, string> = {}
    try {
      const parsed = JSON.parse(receptor?.getCredentials() || '{}')
      Object.entries(parsed || {}).forEach(([k, v]) => {
        if (typeof v === 'string') credentials[k.toLowerCase()] = v
      })
    } catch {
      // Credentials are not always JSON; the notice then names no app id.
    }
    return { identity: identity, credentials }
  }, [receptor, identity])
  const nodeNotice = useCallback(
    (node: DocumentNode) => spec.nodeNotice(node, noticeContext),
    [spec, noticeContext],
  )
  // With several connections of this model the title names the one browsed;
  // with one, the title is the vendor's alone, as before.
  const { data: accountReceptors } = useAccountReceptors()
  const severalConnections = useMemo(
    () =>
      !request?.lockConnection &&
      hasSeveralConnections(accountReceptors?.getReceptorsList(), modelId),
    [accountReceptors, modelId, request],
  )
  // Every enabled connection of this model, offered in the header when
  // there is more than one. The requested row is listed even if disabled,
  // so the select always has a current value.
  const connections = useMemo<ReceptorRecord[]>(() => {
    const requested = request?.receptor
    const enabled = (accountReceptors?.getReceptorsList() || []).filter(
      (r) => r.getModelid() === modelId && r.getIsenabled(),
    )
    if (requested && !enabled.some((r) => r.getId() === requested.getId())) {
      return [requested, ...enabled]
    }
    return enabled
  }, [accountReceptors, modelId, request])
  const serverSearch = !!sourceInfo?.getCapabilities()?.getServerSearch()

  // Files are always listed. In folders mode they cannot be picked, but
  // seeing them is how a user tells which folder holds what a scan should
  // collect.
  // Confluence can also be browsed by label; the tree hook resets itself
  // when the view flips, as it does for a receptor switch.
  const labelsView = !!sourceInfo?.getCapabilities()?.getLabelsView()
  const [byLabel, setByLabel] = useState(false)
  const tree = useDocumentTree(active ? receptorId : undefined, {
    includeFiles: true,
    byLabel: labelsView && byLabel,
  })
  const { state } = tree

  const [currentId, setCurrentId] = useState<string>(ROOT_PARENT_ID)
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set())
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set())
  // In folders mode, "Select this folder" marks the open folder itself.
  const [currentSelected, setCurrentSelected] = useState(false)

  // A receptor with missing or rejected credentials cannot browse. Close with
  // the reason instead of listing into an error.
  useEffect(() => {
    if (!isVisible || !receptor) return
    if (
      blockIfReceptorNeedsCredentials(
        receptor,
        receptor.getName() || getReceptorTemplate(receptor.getModelid()).name,
        credentialsActionForPickerSource(source),
      )
    ) {
      hide()
      if (source !== 'FolderScan') showParentModal()
    }
  }, [isVisible, receptor, source, hide, showParentModal])

  // Fresh state on every open.
  const wasVisible = useRef(false)
  useEffect(() => {
    if (isVisible && !wasVisible.current) {
      tree.reset()
      setSwitchedReceptor(undefined)
      setByLabel(false)
      setCurrentId(ROOT_PARENT_ID)
      setExpandedIds(new Set())
      setSelectedIds(new Set())
      setCurrentSelected(false)
    }
    wasVisible.current = isVisible
    // tree.reset is stable; the effect keys on visibility only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible])

  // `tree` is rebuilt on every state change; the callbacks are stable, so the
  // effect keys on the callback or it would re-list after every failure.
  const { ensureLoaded } = tree
  useEffect(() => {
    if (active) ensureLoaded(ROOT_PARENT_ID)
  }, [active, ensureLoaded])

  // A credentials failure may have just flipped the receptor row; refetch so
  // the guard above can close the picker with the specific reason.
  useEffect(() => {
    if (state.problem?.kind !== 'credentials' || !receptor) return
    invalidateReceptors()
    showInfoToast(
      `${
        receptor.getName() || getReceptorTemplate(receptor.getModelid()).name
      } credentials are invalid or expired. Re-authorize it with "Edit credentials" on the receptor before ${credentialsActionForPickerSource(
        source,
      )}.`,
    )
    hide()
    if (source !== 'FolderScan') showParentModal()
  }, [
    state.problem,
    receptor,
    source,
    invalidateReceptors,
    hide,
    showParentModal,
  ])

  const roots = tree.childrenOf(ROOT_PARENT_ID)
  const current =
    currentId === ROOT_PARENT_ID ? null : state.nodesById[currentId]
  const unsortedListing = state.searchResults
    ? state.searchResults.map((id) => state.nodesById[id]).filter(Boolean)
    : tree.childrenOf(currentId)
  // The listing's order: by name first, as the tree is; a click on a column
  // starts it the way that column reads best (names A to Z, dates newest
  // first) and a second click flips it.
  const [sortCol, setSortCol] = useState<ListSortColumn>('name')
  const [sortType, setSortType] = useState<GridColumnSortType>(
    GridColumnSortType.ASCENDING,
  )
  const onSort = (col: ListSortColumn): void => {
    if (col === sortCol) {
      setSortType(getNextColSortType(sortType))
      return
    }
    setSortCol(col)
    setSortType(
      col === 'modified'
        ? GridColumnSortType.DESCENDING
        : GridColumnSortType.ASCENDING,
    )
  }
  const listing = useMemo(
    () => sortNodes(unsortedListing, sortCol, sortType),
    [unsortedListing, sortCol, sortType],
  )
  const listingLoading =
    !state.searchResults &&
    !state.loaded[currentId] &&
    !!state.loading[currentId]
  const path = current ? tree.pathTo(current.id) : []

  const openNode = useCallback(
    (node: DocumentNode): void => {
      if (!isContainerLike(node)) return
      setCurrentId(node.id)
      setCurrentSelected(false)
      setExpandedIds((prev) => {
        const next = new Set(prev)
        tree.pathTo(node.id).forEach((n) => next.add(n.id))
        return next
      })
      tree.ensureLoaded(node.id)
    },
    [tree],
  )

  const jumpRoot = useCallback((): void => {
    setCurrentId(ROOT_PARENT_ID)
    setCurrentSelected(false)
  }, [])

  const toggleExpanded = useCallback(
    (node: DocumentNode): void => {
      setExpandedIds((prev) => {
        const next = new Set(prev)
        if (next.has(node.id)) next.delete(node.id)
        else {
          next.add(node.id)
          tree.ensureLoaded(node.id)
        }
        return next
      })
    },
    [tree],
  )

  const toggleSelected = useCallback(
    (node: DocumentNode): void => {
      setCurrentSelected(false)
      setSelectedIds((prev) => {
        const next = new Set(prev)
        if (foldersMode) {
          // One folder-scan root at a time.
          if (next.has(node.id)) next.clear()
          else {
            next.clear()
            next.add(node.id)
          }
          return next
        }
        if (next.has(node.id)) next.delete(node.id)
        else next.add(node.id)
        return next
      })
    },
    [foldersMode],
  )

  const isSelectable = useCallback(
    (node: DocumentNode): boolean =>
      foldersMode
        ? isContainerLike(node) && spec.isScanRoot(node)
        : isLinkable(node),
    [foldersMode, spec],
  )

  const selectableInView = listing.filter(isSelectable)
  const selectAllInView = (): void => {
    setCurrentSelected(false)
    setSelectedIds(
      (prev) => new Set([...prev, ...selectableInView.map((n) => n.id)]),
    )
  }
  const deselectAll = (): void => {
    setCurrentSelected(false)
    setSelectedIds(new Set())
  }
  const selectCurrent = (): void => {
    if (!current) return
    setSelectedIds(new Set([current.id]))
    setCurrentSelected(true)
  }

  const selectedNodes = useMemo(
    () =>
      [...selectedIds]
        .map((id) => state.nodesById[id])
        .filter((n): n is DocumentNode => !!n),
    [selectedIds, state.nodesById],
  )

  const { clearProblem, reload } = tree
  const problemParentId = state.problem?.parentId
  const retryProblem = useCallback((): void => {
    clearProblem()
    reload(problemParentId ?? currentId)
  }, [clearProblem, reload, problemParentId, currentId])

  const handleCancel = (): void => {
    hide()
    if (source !== 'FolderScan') showParentModal()
  }

  // Browse another connection of the same model: a different tree (the tree
  // hook resets itself on the receptor id), so the navigation and selection
  // start over. A connection that cannot browse is refused with the reason
  // and the current one stays.
  const switchConnection = (next: ReceptorRecord): void => {
    if (next.getId() === receptorId) return
    if (
      blockIfReceptorNeedsCredentials(
        next,
        documentSourceLabel(next),
        credentialsActionForPickerSource(source),
      )
    ) {
      return
    }
    setSwitchedReceptor(next)
    setByLabel(false)
    setCurrentId(ROOT_PARENT_ID)
    setExpandedIds(new Set())
    setSelectedIds(new Set())
    setCurrentSelected(false)
  }

  const switchView = (next: 'tree' | 'labels'): void => {
    const nextByLabel = next === 'labels'
    if (nextByLabel === byLabel) return
    setByLabel(nextByLabel)
    setCurrentId(ROOT_PARENT_ID)
    setExpandedIds(new Set())
    setSelectedIds(new Set())
    setCurrentSelected(false)
  }

  const handleAdd = (): void => {
    if (!receptor) return
    if (selectedNodes.length === 0) {
      showInfoToast(
        foldersMode
          ? `Select a ${spec.label} folder first`
          : 'Select at least one document',
      )
      return
    }
    if (foldersMode && selectedNodes.length !== 1) {
      showInfoToast(`Select one ${spec.label} folder`)
      return
    }
    setSelection({ receptor, source, nodes: selectedNodes })
    hide()
    showParentModal()
  }

  const searchCandidates = useMemo<DocumentNode[]>(() => {
    if (serverSearch) return state.searchResults ? listing : []
    return Object.values(state.nodesById).filter((n) =>
      foldersMode ? isContainerLike(n) : true,
    )
  }, [serverSearch, state.searchResults, listing, state.nodesById, foldersMode])

  const onSearchPick = (node: DocumentNode): void => {
    if (isContainerLike(node)) {
      openNode(node)
      return
    }
    // A document: go to its parent and select it.
    if (node.parentId !== ROOT_PARENT_ID && state.nodesById[node.parentId]) {
      openNode(state.nodesById[node.parentId])
    } else {
      setCurrentId(node.parentId)
      tree.ensureLoaded(node.parentId)
    }
    if (!foldersMode) setSelectedIds((prev) => new Set([...prev, node.id]))
  }

  if (!request) return null

  const baseTitle = foldersMode
    ? `Choose a ${spec.label} folder`
    : `Add ${spec.label} ${source === 'Policy' ? 'documents' : 'evidence'}`
  // With several connections the title carries the chooser itself:
  // "Add Google Drive evidence from [gd2 v]".
  const title = severalConnections ? (
    <>
      {baseTitle} from
      <ConnectionSelect
        receptors={connections}
        value={receptorId || ''}
        onChange={switchConnection}
      />
    </>
  ) : (
    baseTitle
  )
  const shareHint = identity ? spec.shareHint(identity) : ''

  return (
    <ModalForm
      hide={handleCancel}
      show={isVisible}
      formId={ModalFormId.DOCUMENT_PICKER}
      title={title}
      backdrop="static"
      hideButtons
    >
      <Stack spacing={1}>
        {labelsView && (
          <ViewToggle
            value={byLabel ? 'labels' : 'tree'}
            treeLabel={spec.rootCaption}
            onChange={switchView}
          />
        )}
        {shareHint && (
          <Typography variant="caption" color="text.secondary">
            {shareHint}
          </Typography>
        )}

        {state.problem && (
          <ProblemBanner
            key={`${state.problem.kind}:${state.problem.parentId ?? ''}`}
            problem={state.problem}
            vendorLabel={spec.label}
            identity={identity}
            onRetry={retryProblem}
          />
        )}

        <BrowserRoot direction="row" spacing={0} sx={{ height: 520 }}>
          <TreePane
            roots={roots}
            notice={nodeNotice}
            childrenOf={tree.childrenOf}
            isExpandable={tree.isExpandable}
            isLoading={(id) => !!state.loading[id]}
            expandedIds={expandedIds}
            currentId={current?.id ?? null}
            onSelect={openNode}
            onToggle={toggleExpanded}
          />

          <RightPane>
            <RightPaneHeader direction="row" alignItems="center" spacing={1}>
              <PathBreadcrumbs
                path={path}
                rootCaption={
                  spec.rootCaptionFor
                    ? spec.rootCaptionFor(roots)
                    : spec.rootCaption
                }
                onJump={openNode}
                onJumpRoot={jumpRoot}
              />
              <SearchBox
                serverSearch={serverSearch}
                searching={state.searching}
                candidates={searchCandidates}
                onSearch={tree.search}
                onPick={onSearchPick}
                placeholder={
                  serverSearch ? `Search ${spec.label}` : 'Search loaded items'
                }
              />
            </RightPaneHeader>

            <RightPaneDivider />

            <ListPane
              nodes={listing}
              notice={nodeNotice}
              sort={{ col: sortCol, type: sortType, onSort }}
              loading={
                listingLoading || (state.searching && !state.searchResults)
              }
              hasMore={!state.searchResults && !!state.nextToken[currentId]}
              loadingMore={
                !!state.loading[currentId] && !!state.loaded[currentId]
              }
              onLoadMore={() => tree.loadMore(currentId)}
              selectedIds={selectedIds}
              isSelectable={isSelectable}
              onToggleSelected={toggleSelected}
              onOpen={openNode}
              emptyText={
                state.searchResults
                  ? 'No matches.'
                  : current
                  ? `This ${spec.containerNoun(current)} is empty.`
                  : `Nothing is shared with this connection yet.`
              }
            />

            <RightPaneDivider />

            <RightPaneFooter>
              <FooterActions direction="row" spacing={1} alignItems="center">
                {foldersMode && current && spec.isScanRoot(current) ? (
                  <Tooltip
                    title={`Use ${current.title} as the folder scan root`}
                  >
                    <span>
                      <Button
                        size="small"
                        aria-pressed={currentSelected}
                        onClick={selectCurrent}
                      >
                        Select this {spec.containerNoun(current)}
                      </Button>
                    </span>
                  </Tooltip>
                ) : !foldersMode ? (
                  <Button
                    size="small"
                    onClick={selectAllInView}
                    disabled={selectableInView.length === 0}
                  >
                    Select all
                  </Button>
                ) : null}
                <Button
                  size="small"
                  onClick={deselectAll}
                  disabled={selectedIds.size === 0}
                >
                  Deselect all
                </Button>
                <Box sx={{ flex: 1 }} />
                {selectedNodes.length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {foldersMode
                      ? selectedNodes[0].title
                      : `${selectedNodes.length} selected`}
                  </Typography>
                )}
              </FooterActions>
            </RightPaneFooter>
          </RightPane>
        </BrowserRoot>

        <Divider sx={{ my: 1 }} />
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <SecondaryButton
            text="Cancel"
            onClick={handleCancel}
            size={StandardButtonSize.SMALL}
          />
          <PrimaryButton
            text="Add"
            onClick={handleAdd}
            size={StandardButtonSize.SMALL}
          />
        </Stack>
      </Stack>
    </ModalForm>
  )
}
